/* eslint camelcase: "off" */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  EmptyState,
  SearchInput,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@jem-open/jem-ui';
import { ArrowLeft, Folder, Loader2 } from 'lucide-react';

import apiResponseHandler from '../../../../utils/apiResponseHandler';
import { verificationClient } from '../../../../services/verificationClient';
import { PolicyAction } from '../../../../types/VerificationTypes';

import ActionCategoriesSection from './ActionCategoriesSection';


type View = 'actions' | 'categories';


/**
 * Read-only actions registry. Mutations happen via Django seed migrations.
 *
 * Designed for embedding inside the Action Policies tab. Switches between
 * the actions list and the action-categories registry via the
 * "View Categories" button.
 */
const ActionsSection: React.FC = () => {
  const [view, setView] = useState<View>('actions');
  const [rows, setRows] = useState<PolicyAction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await verificationClient.listActions());
    } catch (err) {
      apiResponseHandler.handleApiError(err);
      setError('Could not load actions.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (view === 'actions') load();
  }, [load, view]);

  const filtered = useMemo(() => {
    if (!filter) return rows;
    const q = filter.toLowerCase();
    return rows.filter(
      (r) =>
        r.code.toLowerCase().includes(q) ||
        r.name.toLowerCase().includes(q) ||
        r.category.code.toLowerCase().includes(q)
    );
  }, [rows, filter]);

  if (view === 'categories') {
    return (
      <div>
        <div className="mb-sm">
          <Button
            variant="link"
            size="sm"
            leftIcon={<ArrowLeft size={14} />}
            onClick={() => setView('actions')}
          >
            Back to Actions
          </Button>
        </div>
        <ActionCategoriesSection />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-sm flex items-center gap-sm">
        <div className="flex-1">
          <SearchInput
            placeholder="Filter by code, name, or category…"
            value={filter}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilter(e.target.value)}
            onClear={() => setFilter('')}
          />
        </div>
        <Button
          variant="outline"
          leftIcon={<Folder size={16} />}
          onClick={() => setView('categories')}
        >
          View Categories
        </Button>
      </div>

      {loading && (
        <div className="flex justify-center py-lg">
          <Loader2 className="animate-spin text-secondary-text-label" size={24} />
        </div>
      )}
      {!loading && error && (
        <EmptyState
          variant="bordered"
          size="sm"
          icon="alert"
          title="Couldn’t load actions"
          description={error}
          actions={<Button onClick={load}>Try again</Button>}
        />
      )}
      {!loading && !error && filtered.length === 0 && (
        <EmptyState
          variant="bordered"
          size="sm"
          icon="search"
          title="No actions match"
          description="Try adjusting your filter, or clear it to see all actions."
        />
      )}
      {!loading && !error && filtered.length > 0 && (
        <TooltipProvider delayDuration={150}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Sensitive</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.code}>
                  <TableCell>
                    {r.description ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-help underline decoration-dotted underline-offset-2">
                            {r.name}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">{r.description}</TooltipContent>
                      </Tooltip>
                    ) : (
                      r.name
                    )}
                  </TableCell>
                  <TableCell>
                    <code className="text-xs">{r.code}</code>
                  </TableCell>
                  <TableCell>
                    <Tag variant="processing">{r.category.code}</Tag>
                  </TableCell>
                  <TableCell>{r.is_sensitive ? <Tag variant="pending">yes</Tag> : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TooltipProvider>
      )}
    </div>
  );
};

export default ActionsSection;
